import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

import service1 from "@/assets/service-1.png";
import service2 from "@/assets/service-2.png";
import service3 from "@/assets/service-3.png";
import service4 from "@/assets/service-4.png";
import service6 from "@/assets/service-6.png";
import serviceLandscape from "@/assets/service-landscape.jpg";
import serviceLighting from "@/assets/service-lighting.jpg";
import serviceArtDecor from "@/assets/service-artdecor.jpg";

const services = [
  {
    title: "Architectural Design",
    desc: "We design creative, functional spaces that reflect your vision. From concept to compliance, every detail is thoughtfully planned. The result is timeless architecture with lasting impact.",
    features: ["Conceptual Design", "Schematic Design", "Design Development", "3D Visualization and Rendering"],
    image: service1,
  },
  {
    title: "Interior Design",
    desc: "We create elegant and functional interiors that maximize space and aesthetics. With careful attention to detail, we plan every aspect from layout to finishes. Our designs are tailored to fit your lifestyle and preferences.",
    features: ["Space Planning", "Furniture Layout", "Material & Color Selection", "Custom Detailing"],
    image: service2,
  },
  {
    title: "Construction Drawings",
    desc: "Our comprehensive construction drawings ensure precise execution. They include detailed plans, sections, and elevations for smooth coordination. These drawings are essential for accurate construction and approvals.",
    features: ["Working Drawings", "Detailed Section Drawings", "Elevations and Joinery Details", "Structural Coordination"],
    image: service3,
  },
  {
    title: "Project Management",
    desc: "From budgeting to final execution, our project management keeps everything on track. We coordinate schedules, teams, and quality checks to ensure timely delivery. Your peace of mind is our priority at every stage.",
    features: ["Budget Planning", "Timeline and Scheduling", "Site Coordination", "Quality Control"],
    image: service4,
  },
  {
    title: "Landscape",
    desc: "We design outdoor spaces that blend nature with functionality. Our landscape concepts enhance the beauty, comfort, and sustainability of every project, ensuring a serene environment for living and leisure.",
    features: ["Garden Design", "Hardscape Planning", "Water Features", "Sustainable Landscaping"],
    image: serviceLandscape,
  },
  {
    title: "Renovation & Restoration",
    desc: "We revive and reimagine existing structures through thoughtful renovation. Our expertise includes adaptive reuse and heritage restoration. We strengthen buildings with structural retrofitting while retaining their charm.",
    features: ["Adaptive Reuse", "Heritage Building Restoration", "Structural Retrofitting"],
    image: service6,
  },
  {
    title: "Lighting",
    desc: "We craft lighting solutions that bring life, depth, and emotion to every space. From ambient moods to task-focused illumination, our designs balance aesthetics with functionality for both interiors and exteriors.",
    features: ["Ambient Lighting", "Accent Lighting", "Outdoor Lighting", "Smart Lighting Systems"],
    image: serviceLighting,
  },
  {
    title: "Art & Decor",
    desc: "Our art and décor services transform spaces into reflections of personality and culture. We curate elegant artworks, décor elements, and custom installations that complement the architecture beautifully.",
    features: ["Wall Art & Murals", "Sculptures & Installations", "Decor Accessories", "Custom Styling"],
    image: serviceArtDecor,
  },
];

const featureStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const featureItem = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
};

const servicesJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Architectural Services by Dhivisha Architects",
  "description": "Comprehensive architectural and interior design services offered by COA-registered architects in Kumbakonam, Cauvery Delta region.",
  "numberOfItems": services.length,
  "itemListElement": services.map((s, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "item": {
      "@type": "Service",
      "name": s.title,
      "description": s.desc,
      "provider": { "@id": "https://dhivishaarchitects.com/#organization" },
      "areaServed": { "@type": "AdministrativeArea", "name": "Cauvery Delta Region, Tamil Nadu" },
    },
  })),
};

const Services = () => {
  return (
    <Layout transparentNav>
      <SEOHead
        title="Services — Registered Architect & Interior Design in Kumbakonam"
        description="Comprehensive architectural services by COA-registered architects in Kumbakonam & Delta district: design, interior, construction drawings, project management, landscape, renovation & lighting. End-to-end solutions across the Cauvery Delta region."
        path="/services"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(servicesJsonLd) }}
      />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=70"
            alt="Services"
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
            Our Services
          </h1>
          <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto">
            From vision to reality — comprehensive architectural and design solutions tailored to your needs.
          </p>
        </motion.div>
      </section>

      {/* Services — Alternating Layout */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-4">
              Our Services
            </h2>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-16">
              We offer end-to-end architectural and interior solutions, bringing creativity and precision to every project.
            </p>
          </motion.div>

          <div className="space-y-24">
            {services.map((service, i) => {
              const isReversed = i % 2 !== 0;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5 }}
                  className={`grid md:grid-cols-2 gap-10 items-center ${isReversed ? "md:direction-rtl" : ""}`}
                >
                  {/* Image */}
                  <motion.div
                    className={`overflow-hidden rounded ${isReversed ? "md:order-2" : ""}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[350px] object-cover hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                      decoding="async"
                    />
                  </motion.div>

                  {/* Content */}
                  <div className={isReversed ? "md:order-1" : ""}>
                    <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                      {service.title}
                    </h3>
                    <div className="w-16 h-0.5 bg-primary mb-4" />
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {service.desc}
                    </p>
                    <motion.div
                      className="space-y-3 mb-6"
                      variants={featureStagger}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {service.features.map((f) => (
                        <motion.div
                          key={f}
                          variants={featureItem}
                          className="flex items-center gap-3 p-3 bg-muted rounded border border-border hover:border-primary transition-colors"
                        >
                          <span className="text-primary font-bold text-sm">✦</span>
                          <span className="text-sm font-medium text-foreground">{f}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="hover:bg-primary hover:text-primary-foreground active:scale-95 transition-all"
                      asChild
                    >
                      <a
                        href={`https://wa.me/919994849332?text=${encodeURIComponent(`Hi, I'm interested in your ${service.title} service.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn More <ArrowRight className="ml-1" size={14} />
                      </a>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
